import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { decode } from '../../Services/DecodeToken';
import { vendingTableBodyCellSx, vendingTableHeadCellSx } from './alertDisplayUtils';
import vendingApiClient from '../../Services/VendingApi';
import { COLORS } from '../../Styles/Colors';
import { VendingMachineUser, VendingMachineUserListResponse } from '../../Types/vending';

interface MachineUsersTabProps {
  machineId: string;
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const mapStatusChip = (status: string) => {
  const value = (status || 'unmapped').toLowerCase();
  if (value === 'mapped') {
    return { label: 'Mapped', color: COLORS.success, bg: `${COLORS.success}18` };
  }
  if (value === 'conflict') {
    return { label: 'Conflict', color: COLORS.red, bg: `${COLORS.red}12` };
  }
  if (value === 'ignored') {
    return { label: 'Ignored', color: COLORS.lightGray, bg: `${COLORS.lightGray}18` };
  }
  return { label: 'Unmapped', color: COLORS.orange, bg: `${COLORS.orange}18` };
};

export const MachineUsersTab = ({ machineId }: MachineUsersTabProps) => {
  const token: any = decode();
  const isAbatixAdmin = Number(token?.roles?.level) >= 100;
  const [users, setUsers] = useState<VendingMachineUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendingApiClient(`/machines/${machineId}/users?limit=200`);
      const json: VendingMachineUserListResponse = await response.json();
      setUsers(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => {
      const hay = [
        user.name,
        user.employee_number,
        user.badge_number,
        user.department,
        user.aims_username,
        user.aims_name,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [users, searchInput]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
        }}
      >
        <TextField
          size="small"
          label="Search"
          placeholder="Name, badge, department…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 220, flex: { xs: '1 1 100%', md: '0 1 280px' } }}
        />
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          {isAbatixAdmin && (
            <Button
              component={RouterLink}
              to="/vending/mappings/employees"
              variant="text"
              sx={{ color: COLORS.teal, fontWeight: 600 }}
            >
              Employee Mapping
            </Button>
          )}
          <Button variant="outlined" onClick={loadUsers} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: COLORS.teal }} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadUsers}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && filteredUsers.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            borderRadius: 2,
            border: `1px dashed ${COLORS.lightGray}`,
            backgroundColor: '#fafbfc',
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
            No users assigned
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
            {searchInput.trim()
              ? 'No users match your search.'
              : 'No employees are linked to this machine yet. Run an employees sync to pull assignments from SourceVend.'}
          </Typography>
        </Box>
      )}

      {!loading && !error && filteredUsers.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${COLORS.lightGray}40`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="medium" sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={vendingTableHeadCellSx}>Name</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 120 }}>Employee #</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 120 }}>Badge</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 140 }}>Department</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 160 }}>AIMS user</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 110 }}>Mapping</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 100 }}>Active</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 160 }}>Last vend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  const mapChip = mapStatusChip(user.map_status);
                  const aimsLabel = user.aims_name || user.aims_username;
                  return (
                    <TableRow key={user.id} hover sx={{ '&:hover': { backgroundColor: '#fafbfc' } }}>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name || '—'}
                        </Typography>
                        {user.is_administrator && (
                          <Typography variant="caption" sx={{ color: COLORS.teal }}>Administrator</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {user.employee_number || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>{user.badge_number || '—'}</TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>{user.department || '—'}</TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        {aimsLabel ? (
                          <>
                            <Typography variant="body2">{aimsLabel}</Typography>
                            {user.aims_username && user.aims_name && (
                              <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                                {user.aims_username}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: COLORS.lightGray }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Chip
                          size="small"
                          label={mapChip.label}
                          sx={{ fontWeight: 600, bgcolor: mapChip.bg, color: mapChip.color }}
                        />
                        {isAbatixAdmin && user.map_status !== 'mapped' && (
                          <Link
                            component={RouterLink}
                            to="/vending/mappings/employees"
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: COLORS.teal, fontWeight: 600 }}
                          >
                            Map in admin
                          </Link>
                        )}
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        {user.active == null ? '—' : user.active ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatDate(user.last_vend_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default MachineUsersTab;
